// @ts-nocheck
import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Switch,
  TextField,
  IconButton,
  DialogContent,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import ResetIcon from '@mui/icons-material/Restore';
import { WorkingHoursType } from '../../models/WorkingHours';
import { defaultTimeZone, defaultWorkingHours } from './components/DefaultHours';
import TimezoneSelect from './components/TimezoneSelect';
import { fetchWorkingHours, updateWorkingHours } from './components/WorkingHoursService';
import { ValidateTimes } from './components/TimeValidation';
import LocalizationContext from '../../../shared/contexts/LocalizationContext';
import { LocalizationEntity } from '../../../shared/models/localization';
import { usePreSave } from '../../../../detail-form/PreSaveContext';
import api from '../../../../../services/api';

interface WorkingHoursProps {
  disabled: boolean;
  recordId?: string | number;
}

const WorkingHours: React.FC<WorkingHoursProps> = ({ disabled, recordId }) => {
  const [loading, setLoading] = useState(true);
  const [workingHours, setWorkingHours] = useState<WorkingHoursType[]>(defaultWorkingHours);
  const [timezone, setTimezone] = useState<string>(defaultTimeZone);
  const [validationErrors, setValidationErrors] = useState<Map<string, string | null>>(new Map());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [userId, setUserId] = useState('');
  const localization = useContext<Array<LocalizationEntity>>(LocalizationContext);
  const params = new URLSearchParams(window.location.search);
  const urlId = params.get('id');
  const targetId = recordId || urlId;

  useEffect(() => {
    const loadWorkingHours = async () => {
      try {
        if (targetId) {
          await fetchWorkingHours(String(targetId), setWorkingHours, setTimezone, setLoading);
          return;
        }

        const { data: currentUser } = await api.get('/umbraco/api/UserManagment/GetCurrentUser');
        setUserId(currentUser.Id);
        await fetchWorkingHours(currentUser.Id, setWorkingHours, setTimezone, setLoading);
      } catch (error) {
        console.error('Error loading user or working hours:', error);
      }
    };

    void loadWorkingHours();
  }, [targetId]);

  const handleSave = useCallback(async () => {
    const newValidationErrors = new Map<string, string | null>();

    workingHours.forEach(
      ({ DayOfWeek, StartOfWorkingDay, EndOfWorkingDay, StartOfLunch, EndOfLunch, IsActive }) => {
        if (IsActive) {
          const validationError = ValidateTimes(
            localization,
            StartOfWorkingDay,
            EndOfWorkingDay,
            StartOfLunch ?? '',
            EndOfLunch ?? ''
          );
          newValidationErrors.set(DayOfWeek, validationError);
        } else {
          newValidationErrors.set(DayOfWeek, null);
        }
      }
    );

    setValidationErrors(newValidationErrors);

    let hasErrors = false;
    newValidationErrors.forEach((error) => {
      if (error !== null) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setSnackbarMessage('Please fix validation errors before saving.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      throw new Error('Validation failed: please fix errors before saving.');
    }

    try {
      const idToSave = targetId || userId;
      await updateWorkingHours(idToSave, workingHours, timezone, setWorkingHours, setTimezone);

      setSnackbarMessage('Working hours saved successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Error saving working hours.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error(error);
    }
  }, [workingHours, timezone, targetId, userId, localization]);

  const preSave = usePreSave();

  useEffect(() => {
    if (preSave) {
      return preSave.register(handleSave);
    }
  }, [preSave, handleSave]);

  const resetToDefault = (dayOfWeek: string) => {
    setWorkingHours((prevState) =>
      prevState.map((item) =>
        item.DayOfWeek === dayOfWeek
          ? {
              ...item,
              StartOfWorkingDay: '',
              StartOfLunch: '',
              EndOfLunch: '',
              EndOfWorkingDay: '',
            }
          : item
      )
    );
  };

  const handleChange = (dayOfWeek: string, field: string, value: string) => {
    setWorkingHours((prevState) =>
      prevState.map((item) => (item.DayOfWeek === dayOfWeek ? { ...item, [field]: value } : item))
    );
  };

  const handleSwitchChange = (dayOfWeek: string, checked: boolean) => {
    setWorkingHours((prevState) =>
      prevState.map((item) =>
        item.DayOfWeek === dayOfWeek ? { ...item, IsActive: checked } : item
      )
    );
  };

  return (
    <>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '94%',
          }}
        >
          <TimezoneSelect disabled={disabled} value={timezone} onChange={setTimezone} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 2 }}>
          {workingHours.map(
            ({
              DayOfWeek,
              IsActive,
              StartOfWorkingDay,
              StartOfLunch,
              EndOfLunch,
              EndOfWorkingDay,
            }) => {
              const validationError = validationErrors.get(DayOfWeek);
              const isRowInvalid = validationError !== null && validationError !== undefined;
              return (
                <Box key={DayOfWeek}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
                    <Box sx={{ width: 90, flexShrink: 0 }}>
                      <strong>{DayOfWeek}</strong>
                    </Box>

                    <Box sx={{ width: 56, flexShrink: 0 }}>
                      <Switch
                        checked={IsActive}
                        disabled={disabled}
                        onChange={(e) => handleSwitchChange(DayOfWeek, e.target.checked)}
                        name={`active-${DayOfWeek}`}
                      />
                    </Box>

                    {Boolean(IsActive) && (
                      <>
                        <TextField
                          disabled={disabled}
                          label="Start"
                          type="time"
                          value={StartOfWorkingDay}
                          onChange={(e) =>
                            handleChange(DayOfWeek, 'StartOfWorkingDay', e.target.value)
                          }
                          sx={{ width: 140 }}
                          InputLabelProps={{ shrink: true }}
                          error={isRowInvalid}
                        />
                        <TextField
                          disabled={disabled}
                          label="Lunch Start"
                          type="time"
                          value={StartOfLunch || ''}
                          onChange={(e) => handleChange(DayOfWeek, 'StartOfLunch', e.target.value)}
                          sx={{ width: 140 }}
                          InputLabelProps={{ shrink: true }}
                          error={isRowInvalid}
                        />
                        <TextField
                          disabled={disabled}
                          label="Lunch End"
                          type="time"
                          value={EndOfLunch || ''}
                          onChange={(e) => handleChange(DayOfWeek, 'EndOfLunch', e.target.value)}
                          sx={{ width: 140 }}
                          InputLabelProps={{ shrink: true }}
                          error={isRowInvalid}
                        />
                        <TextField
                          label="End"
                          type="time"
                          disabled={disabled}
                          value={EndOfWorkingDay}
                          onChange={(e) =>
                            handleChange(DayOfWeek, 'EndOfWorkingDay', e.target.value)
                          }
                          sx={{ width: 140 }}
                          InputLabelProps={{ shrink: true }}
                          error={isRowInvalid}
                        />
                        <IconButton onClick={() => resetToDefault(DayOfWeek)}>
                          <ResetIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>

                  {isRowInvalid && validationError && (
                    <Box sx={{ mt: 0.5, ml: '162px' }}>
                      <Typography color="error" variant="body2">
                        {validationError}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            }
          )}
        </Box>
      </DialogContent>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkingHours;
